import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Home, Shield, Briefcase, PhoneCall, Mail, Phone } from 'lucide-react';
import Layout from '../components/Layout';
import ContactForm from '../components/ContactForm';

export default function NotFound() {
  return (
    <Layout>
      <Helmet>
        <title>Siden blev ikke fundet | CM Skruefundament ApS</title>
        <meta name="description" content="Siden du leder efter, findes desværre ikke. Gå tilbage til forsiden eller find vores skruetyper og projekter her." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <section className="bg-slate-900 py-24 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.3em] mb-6 block text-slate-400">Fejl 404</span>
          <h1 className="text-[2.5rem] md:text-[4rem] font-black uppercase leading-tight mb-8">
            Siden blev ikke fundet
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Beklager, men den side du leder efter findes ikke, er blevet slettet eller har fået ny adresse.
          </p>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold uppercase tracking-tight mb-8 text-slate-900">
            Hvad leder du efter?
          </h2>
          <p className="text-slate-600 mb-12">
            Brug genvejene nedenfor til hurtigt at finde vej tilbage til de mest populære sider på vores hjemmeside:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left mb-12">
            <Link 
              to="/" 
              className="flex items-center gap-4 p-5 bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all group"
            >
              <div className="p-3 bg-slate-100 text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                <Home className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold uppercase text-sm tracking-wider text-slate-900">Forside</h3>
                <p className="text-xs text-slate-500">Gå til vores startside</p>
              </div>
              <ArrowRight className="w-4 h-4 ml-auto text-slate-400 group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link 
              to="/skruer" 
              className="flex items-center gap-4 p-5 bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all group"
            >
              <div className="p-3 bg-slate-100 text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold uppercase text-sm tracking-wider text-slate-900">Vores Skruer</h3>
                <p className="text-xs text-slate-500">Se vores skruetyper & specifikationer</p>
              </div>
              <ArrowRight className="w-4 h-4 ml-auto text-slate-400 group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link 
              to="/projekter" 
              className="flex items-center gap-4 p-5 bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all group"
            >
              <div className="p-3 bg-slate-100 text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                <Briefcase className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold uppercase text-sm tracking-wider text-slate-900">Projekter</h3>
                <p className="text-xs text-slate-500">Se vores udvalgte cases</p>
              </div>
              <ArrowRight className="w-4 h-4 ml-auto text-slate-400 group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link 
              to="/kontakt" 
              className="flex items-center gap-4 p-5 bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all group"
            >
              <div className="p-3 bg-slate-100 text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-colors">
                <PhoneCall className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold uppercase text-sm tracking-wider text-slate-900">Kontakt</h3>
                <p className="text-xs text-slate-500">Få et uforpligtende tilbud</p>
              </div>
              <ArrowRight className="w-4 h-4 ml-auto text-slate-400 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-slate-800 transition-colors"
          >
            Gå til forsiden
          </Link>
        </div>
      </section>

      {/* Help / Contact Section */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500 mb-2 block">Brug for hjælp?</span>
            <h2 className="text-2xl font-bold uppercase tracking-tight text-slate-900 mb-3">
              Send os en besked direkte
            </h2>
            <p className="text-slate-600 text-sm max-w-xl mx-auto">
              Kunne du ikke finde det du ledte efter? Udfyld formularen herunder eller ring på <a href="tel:26796139" className="font-bold text-slate-900 underline">26 79 61 39</a>, så hjælper vi dig videre med det samme.
            </p>
          </div>
          <div className="bg-slate-50 p-6 sm:p-10 rounded-2xl border border-slate-200 shadow-lg">
            <ContactForm />
          </div>
        </div>
      </section>
    </Layout>
  );
}
